import { useAuth } from '@tom-ui/utils'
import { FormGroup, FormHelperText, Stack, Typography } from '@mui/material'
import { HatchCoverMoveDirection } from '@operations/app/api'
import { HatchCoverFormData } from '@operations/stores/HatchCoverMoveStore'
import { useTranslate } from '@tolgee/react'
import { FieldboxSelectionGroup, FieldboxWithRadio, useMinimalsTheme } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'

interface IProps {
  formId: string
  vesselVisitId: number
  onSubmit: (formData: HatchCoverFormData) => Promise<void>
}

export const HatchCoverMoveForm = observer(({ formId, vesselVisitId, onSubmit }: IProps) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const { user } = useAuth()

  const defaultValues: HatchCoverFormData = useMemo(() => {
    return {
      id: undefined,
      direction: HatchCoverMoveDirection.Open,
      equipmentId: undefined,
      carrierVisitId: vesselVisitId,
      operator: user?.name ?? '',
    }
  }, [user?.name, vesselVisitId])

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<HatchCoverFormData>({
    defaultValues: defaultValues,
  })

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack gap={theme.customSpacing.m}>
        <FormGroup sx={{ gap: theme.customSpacing.xs }}>
          <Typography variant='subtitle2'>
            {t('hatchCoverMoveSelection', 'Hatch cover move selection')}
          </Typography>

          <Controller
            control={control}
            name='direction'
            rules={{ required: true }}
            render={({ field }) => (
              <FieldboxSelectionGroup
                onSelect={event => {
                  field.onChange(event)
                }}
                defaultValue={field.value}
                options={controller => (
                  <>
                    <FieldboxWithRadio
                      selectionKey={HatchCoverMoveDirection.Open}
                      controller={controller}
                      label={t('opened', 'Opened')}
                      dataCy='hatch-cover-move-opened'
                    />
                    <FieldboxWithRadio
                      selectionKey={HatchCoverMoveDirection.Close}
                      controller={controller}
                      label={t('closed', 'Closed')}
                      dataCy='hatch-cover-move-closed'
                    />
                  </>
                )}
              />
            )}
          />

          {errors['direction'] && (
            <FormHelperText error>{t('fieldIsRequired', 'Field is required')}</FormHelperText>
          )}
        </FormGroup>
      </Stack>
    </form>
  )
})
